import { createAsyncThunk } from "@reduxjs/toolkit";
import type { RootState } from "../../dataManagement/uiDb/UiDb";
import BusinessSectionApiService from "../apiServices/BusinessSectionApiService";
import {
  CreateBusinessSectionPayload,
  ReorderBusinessSectionsPayload,
} from "../../interfaces/BusinessSectionObject";
import { isSectionOrDescendant } from "../helpers/BusinessSectionTreeHelper";

function rejection(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

/**
 * Full nested tree for a business — public endpoint, no auth needed.
 *
 * Callers should check `state.businessSections.treesByBusinessId[businessId]`
 * first and only dispatch this when it's missing (the picker, the sections
 * manager, and the bulk-upload flows all share this one cache). The thunk
 * itself still doesn't self-skip on an EXISTING cache entry — that's the
 * callers' job, matching every other fetch thunk in this codebase
 * (fetchMyBusinesses, fetchBusinessById, ...).
 *
 * It DOES guard against a concurrent duplicate: `condition` skips dispatching
 * entirely (no pending/fulfilled/rejected at all) while a fetch for the same
 * businessId is already in flight. Without this, two screens mounting for the
 * same business in the same tick (e.g. SectionsManager and SectionTreePicker,
 * if a future screen ever shows both at once) would each read the same stale
 * `isLoading: false` snapshot and both fire a request.
 */
export const fetchSectionTree = createAsyncThunk(
  "businessSections/fetchSectionTree",
  async (businessId: string, { rejectWithValue }) => {
    try {
      const tree = await BusinessSectionApiService.getSectionTree(businessId);
      return { businessId, tree };
    } catch (error) {
      return rejectWithValue(rejection(error));
    }
  },
  {
    condition: (businessId, { getState }) => {
      const state = getState() as RootState;
      return !state.businessSections.loadingMap[businessId];
    },
  }
);

export const createSection = createAsyncThunk(
  "businessSections/createSection",
  async (params: CreateBusinessSectionPayload, { rejectWithValue }) => {
    try {
      const created = await BusinessSectionApiService.createSection(params);
      return { businessId: params.businessId, created };
    } catch (error) {
      return rejectWithValue(rejection(error));
    }
  }
);

export interface RenameOrMoveSectionThunkParams {
  businessId: string;
  sectionId: string;
  /** Full replace — always pass the section's CURRENT name/parentSectionId
   *  for whichever field isn't actually changing (see
   *  RenameOrMoveBusinessSectionPayload's doc comment). */
  name: string;
  parentSectionId: string | null;
  displayOrder?: number | null;
}

export const renameOrMoveSection = createAsyncThunk(
  "businessSections/renameOrMoveSection",
  async (params: RenameOrMoveSectionThunkParams, { getState, rejectWithValue }) => {
    // Only a move to a non-null parent needs the check — moving a section to
    // become a new ROOT (parentSectionId: null) can never create a cycle,
    // and neither can a plain rename, which resends its own current
    // parentSectionId unchanged (see the param's doc comment).
    if (params.parentSectionId !== null) {
      const tree = (getState() as RootState).businessSections.treesByBusinessId[params.businessId];
      if (
        tree &&
        isSectionOrDescendant(tree, params.sectionId, params.parentSectionId)
      ) {
        // Moving a section under itself or one of its own subsections would
        // splice the tree into a cycle — a node that is its own ancestor —
        // and every recursive walk over it (rendering, findSectionById, the
        // drill-in picker) would loop or blow the stack the moment it
        // reached that node. Caught here, before the API call, rather than
        // trusting the backend to reject it.
        return rejectWithValue("Can't move a section under itself or one of its own subsections.");
      }
    }
    try {
      const updated = await BusinessSectionApiService.renameOrMoveSection(
        params.sectionId,
        params
      );
      return { businessId: params.businessId, updated };
    } catch (error) {
      return rejectWithValue(rejection(error));
    }
  }
);

export interface DeleteSectionThunkParams {
  businessId: string;
  sectionId: string;
  /** true also deletes nested subsections; any product under any of them has
   *  its section cleared (not deleted). Default false fails with
   *  HAS_CHILDREN/HAS_PRODUCTS if the section isn't empty. */
  cascade?: boolean;
}

export const deleteSection = createAsyncThunk(
  "businessSections/deleteSection",
  async (params: DeleteSectionThunkParams, { rejectWithValue }) => {
    try {
      await BusinessSectionApiService.deleteSection(params.sectionId, params.cascade);
      return { businessId: params.businessId, sectionId: params.sectionId };
    } catch (error) {
      return rejectWithValue(rejection(error));
    }
  }
);

export const reorderSections = createAsyncThunk(
  "businessSections/reorderSections",
  async (params: ReorderBusinessSectionsPayload, { rejectWithValue }) => {
    try {
      const siblings = await BusinessSectionApiService.reorderSections(params);
      return {
        businessId: params.businessId,
        parentSectionId: params.parentSectionId,
        siblings,
      };
    } catch (error) {
      return rejectWithValue(rejection(error));
    }
  }
);

const BusinessSectionsThunk = {
  fetchSectionTree,
  createSection,
  renameOrMoveSection,
  deleteSection,
  reorderSections,
};

export default BusinessSectionsThunk;
